import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Paper, Tooltip, IconButton, Divider, useMediaQuery, Card, Chip } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { styled } from '@mui/material/styles';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { calendarApi, CalendarDayStatusDto, WeekOffRuleDto } from '../../api/calendarApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { leaveApi } from '../../api/leaveApi';
import { employeeApi } from '../../api/employeeApi';

const statusColors: Record<string, string> = {
  Present: '#43a047',
  Absent: '#e53935',
  HalfDay: '#ffca28',
  Leave: '#ffb300',
  WeekOff: '#2196f3',
  Holiday: '#ff9800',
  CompOff: '#8e24aa',
  'Days To Come': '#e0e0e0',
};

const DayCell = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{
  status: string;
}>(({ theme, status }) => ({
  backgroundColor: statusColors[status] || statusColors['Days To Come'],
  color: status === 'Days To Come' ? '#888' : '#fff',
  height: 54,
  width: 54,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  margin: 4,
  cursor: 'pointer',
  borderRadius: 16,
  boxShadow: '0 2px 8px rgba(33,150,243,0.07)',
  fontWeight: 600,
  fontSize: 15,
  position: 'relative',
  transition: 'transform 0.1s',
  '&:hover': {
    transform: 'scale(1.07)',
    zIndex: 2,
  },
}));

type CalendarDay = CalendarDayStatusDto & {
  timeDetails?: string;
  checkInTime?: string;
  checkOutTime?: string;
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const statusOrder = [
  'Present', 'Absent', 'HalfDay', 'Holiday', 'Leave', 'WeekOff', 'CompOff', 'Days To Come',
];

const canApplyWeekOffFallback = (status?: string) =>
  !status || status === 'Absent' || status === 'Days To Come' || status === 'Days to come';

const LeaveCalendar: React.FC<{ employeeId?: string }> = ({ employeeId }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [days, setDays] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [weekOffRules, setWeekOffRules] = useState<WeekOffRuleDto[]>([]);
  const [leaveBalance, setLeaveBalance] = useState(0);
  const isMobile = useMediaQuery('(max-width:900px)');

  const today = new Date();

  function getWeekOfMonth(date: Date) {
    const day = date.getDate();
    return Math.floor((day - 1) / 7) + 1;
  }

  const calendarDays: CalendarDay[] = [];
  for (let d = 1; d <= new Date(year, month, 0).getDate(); d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    let dayData = days.find((x) => x.date.startsWith(dateStr));
    const cellDate = new Date(year, month - 1, d);
    const dow = cellDate.getDay();
    const weekOfMonth = getWeekOfMonth(cellDate);
    const backendDayOfWeek = dow === 0 ? 7 : dow;
    const isWeekOff = weekOffRules.some(rule => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth);
    if (!dayData) {
      if (isWeekOff) {
        dayData = { date: dateStr, status: 'WeekOff' };
      } else if (cellDate > today) {
        dayData = { date: dateStr, status: 'Days To Come' };
      } else {
        dayData = { date: dateStr, status: 'Absent' };
      }
    } else {
      if (cellDate > today && dayData.status === 'Absent') {
        dayData = { ...dayData, status: '' };
      }
      if (isWeekOff && canApplyWeekOffFallback(dayData.status)) {
        dayData = { ...dayData, status: 'WeekOff' };
      }
    }
    calendarDays.push(dayData);
  }

  const statusCounts = calendarDays.reduce<Record<string, number>>((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDate = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
        const empId = employeeId || user?.userId;
        const currentEmployee = await employeeApi.getCurrentEmployee();
        const leaveBal = await leaveApi.getLeaveBalance({ employeeId: currentEmployee.id });
        const balance = leaveBal.reduce((acc, lb) => acc + lb.remainingDays, 0);
        setLeaveBalance(balance);
        let mapped: CalendarDay[] = [];
        let weekOff: WeekOffRuleDto[] = [];
        if (empId) {
          const [apiDays, apiWeekOff] = await Promise.all([
            calendarApi.getEmployeeCalendarStatus(empId, startDate, endDate),
            calendarApi.getEmployeeWeekOffRules(empId)
          ]);
          weekOff = apiWeekOff;
          mapped = apiDays.map((d: any) => {
            let timeDetails = '';
            const checkIn = d.checkInTime ?? d.checkinTime ?? d.check_in_time ?? '';
            const checkOut = d.checkOutTime ?? d.checkoutTime ?? d.check_out_time ?? '';
            if (d.status === 'Present' && (checkIn || checkOut)) {
              timeDetails = `${checkIn}${checkIn && checkOut ? ' - ' : ''}${checkOut}`;
            }
            return { ...d, timeDetails, checkInTime: checkIn, checkOutTime: checkOut };
          });
        }
        setWeekOffRules(weekOff);
        setDays(mapped);
      } catch {
        setDays([]);
        setWeekOffRules([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [employeeId, user?.userId, month, year]);

  // Calendar grid weeks
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startDay = firstDay.getDay();
  const totalDays = lastDay.getDate();
  const weeks: (CalendarDay | undefined)[][] = [];
  let week: (CalendarDay | undefined)[] = Array(startDay).fill(undefined);
  for (let d = 1; d <= totalDays; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    let dayData = days.find((x) => x.date.startsWith(dateStr));
    const cellDate = new Date(year, month - 1, d);
    const dow = cellDate.getDay();
    const weekOfMonth = getWeekOfMonth(cellDate);
    const backendDayOfWeek = dow === 0 ? 7 : dow;
    const isWeekOff = weekOffRules.some(rule => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth);
    if (!dayData) {
      if (isWeekOff) {
        dayData = { date: dateStr, status: 'WeekOff' };
      } else if (cellDate > today) {
        dayData = { date: dateStr, status: 'Days To Come' };
      } else {
        dayData = { date: dateStr, status: 'Absent' };
      }
    } else {
      if (cellDate > today && dayData.status === 'Absent') {
        dayData = { ...dayData, status: '' };
      }
      if (isWeekOff && canApplyWeekOffFallback(dayData.status)) {
        dayData = { ...dayData, status: 'WeekOff' };
      }
    }
    week.push(dayData);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }

  if (week.length) {
  // Fill the last row with undefined to complete 7 cells
  while (week.length < 7) {
    week.push(undefined);
  }
  weeks.push(week);
  }
  
  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else { setMonth((m) => m - 1); }
  };
  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else { setMonth((m) => m + 1); }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 3,
        bgcolor: '#f0f1f5',
        p: isMobile ? 1 : 4,
        borderRadius: 4,
      }}
    >
      {/* Main Calendar Card */}
      <Card
        sx={{
          flex: 2,
          borderRadius: 4,
          boxShadow: '0 4px 24px #b3c6e022',
          p: 3,
          bgcolor: '#fff',
          minWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* ── Header: Title + Month Navigation ── */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
            px: 1.5,
            py: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #e3f0ff 0%, #f0f4ff 100%)',
            boxShadow: '0 2px 8px rgba(33,150,243,0.08)',
          }}
        >
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#2e3a59', letterSpacing: 0.5, fontSize: 20 }}
          >
            Attendance Calendar
          </Typography>

          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              onClick={handlePrevMonth}
              size="small"
              sx={{
                bgcolor: '#fff',
                boxShadow: '0 1px 4px #b3c6e033',
                '&:hover': { bgcolor: '#e3f0ff' },
              }}
            >
              <ArrowBackIos fontSize="small" />
            </IconButton>

            <Typography
              variant="h6"
              sx={{
                mx: 1.5,
                minWidth: 170,
                textAlign: 'center',
                fontWeight: 700,
                color: '#2e3a59',
                fontSize: 17,
                letterSpacing: 0.3,
              }}
            >
              {monthNames[month - 1]} {year}
            </Typography>

            <IconButton
              onClick={handleNextMonth}
              size="small"
              sx={{
                bgcolor: '#fff',
                boxShadow: '0 1px 4px #b3c6e033',
                '&:hover': { bgcolor: '#e3f0ff' },
              }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ── Calendar Grid ── */}
        {loading ? (
          <CircularProgress sx={{ mt: 4 }} />
        ) : (
          <>
            <Box display="flex" flexDirection="column" alignItems="center" width="100%">
              {/* Day-of-week headers */}
              <Box display="flex" mb={0.5}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <Box
                      key={d}
                      sx={{
                        width: 62,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        color: '#8a8a8a',
                        fontSize: 13,
                        flexShrink: 0,
                        letterSpacing: 0.3,
                      }}
                    >
                      {d}
                    </Box>
                  ))}
                </Box>
              {/* Weeks */}
              {weeks.map((week, i) => (
                <Box key={i} display="flex">
                  {week.map((day, j) =>
                    day ? (
                      <Tooltip
                        key={j}
                        title={day.status + (day.timeDetails ? ` (${day.timeDetails})` : '')}
                        arrow
                      >
                        <DayCell status={day.status as string}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 15 }}>
                            {new Date(day.date).getDate()}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, fontSize: 10, lineHeight: 1, letterSpacing: 0.2 }}
                          >
                            {day.status?.toUpperCase()}
                          </Typography>
                          {day.timeDetails && (
                            <Typography variant="caption" sx={{ fontSize: 9, opacity: 0.85 }}>
                              {day.timeDetails}
                            </Typography>
                          )}
                        </DayCell>
                      </Tooltip>
                    ) : (
                      // ✅ Empty cell — must match DayCell total size: 54px + 4px margin each side = 62px wide, 54px + 8px = 62px tall
                      <Box
                        key={j}
                        sx={{
                          width: 54,
                          height: 54,
                          margin: '4px',
                          borderRadius: 4,
                          bgcolor: '#f0f1f5',
                          flexShrink: 0,
                        }}
                      />
                    )
                  )}
                </Box>
              ))}
            </Box>
            {/* ── Attendance Summary Chips ── */}
            <Box
              mt={3}
              width="100%"
              display="flex"
              gap={2}
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
              bgcolor="#f6f7fb"
              p={2}
              borderRadius={3}
            >
              <Chip
                icon={
                  <span
                    style={{
                      background: statusColors.Present,
                      borderRadius: '50%',
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                    }}
                  />
                }
                label={`Present Days  ${statusCounts['Present'] || 0}`}
                sx={{ fontWeight: 700, bgcolor: '#e8f5e9', color: '#388e3c', fontSize: 15 }}
              />
              <Chip
                icon={
                  <span
                    style={{
                      background: '#08fc00',
                      borderRadius: '50%',
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                    }}
                  />
                }
                label={`Leave Balance  ${leaveBalance || 0}`}
                sx={{ fontWeight: 700, bgcolor: '#fffde7', color: '#08fc00', fontSize: 15 }}
              />
              <Chip
                icon={
                  <span
                    style={{
                      background: statusColors.WeekOff,
                      borderRadius: '50%',
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                    }}
                  />
                }
                label={`WeekOffs  ${statusCounts['WeekOff'] || 0}`}
                sx={{ fontWeight: 700, bgcolor: '#e3f2fd', color: '#1976d2', fontSize: 15 }}
              />
            </Box>
          </>
        )}
      </Card>

      {/* ── Sidebar ── */}
      <Box sx={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Attendance Summary List */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px #b3c6e022', p: 2.5, bgcolor: '#fff' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#2e3a59' }}>
            Attendance
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {statusOrder.map((status) => (
            <Box key={status} display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Paper
                  sx={{
                    width: 14,
                    height: 14,
                    background: statusColors[status],
                    mr: 0.5,
                    borderRadius: '50%',
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{status}</Typography>
              </Box>
              <Typography variant="body2">{statusCounts[status] || 0}</Typography>
            </Box>
          ))}
        </Card>

        {/* Attendance Overview Pie Chart */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px #b3c6e022', p: 2.5, bgcolor: '#fff' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#2e3a59' }}>
            Attendance Overview
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ position: 'relative', width: 180, height: 180, mx: 'auto' }}>
            <PieChart
              series={[{
                data: [
                  { id: 0, value: statusCounts['Present'] || 0, label: 'Present', color: statusColors.Present },
                  { id: 1, value: statusCounts['Absent'] || 0, label: 'Absent', color: statusColors.Absent },
                  { id: 2, value: statusCounts['HalfDay'] || 0, label: 'HalfDay', color: statusColors.HalfDay },
                  { id: 3, value: statusCounts['Holiday'] || 0, label: 'Holiday', color: statusColors.Holiday },
                  { id: 4, value: statusCounts['Leave'] || 0, label: 'Leave', color: statusColors.Leave },
                  { id: 5, value: statusCounts['WeekOff'] || 0, label: 'WeekOff', color: statusColors.WeekOff },
                  { id: 6, value: statusCounts['CompOff'] || 0, label: 'CompOff', color: statusColors.CompOff },
                ],
                innerRadius: 40,
                outerRadius: 60,
                paddingAngle: 2,
              }]}
              width={180}
              height={180}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="h4" fontWeight={800} color="#43a047" sx={{ lineHeight: 1 }}>
                {(() => {
                  const validStatuses = ['Present', 'Absent', 'HalfDay', 'Holiday', 'Leave', 'WeekOff', 'CompOff'];
                  const validDays = calendarDays.filter(day => validStatuses.includes(day.status));
                  const presentDays = validDays.filter(day => day.status === 'Present').length;
                  const total = validDays.length;
                  return total > 0 ? `${Math.round((presentDays / total) * 100)}%` : '0%';
                })()}
              </Typography>
              <Typography variant="caption" color="#888">Attendance</Typography>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default LeaveCalendar;
